import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Loader2,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Sun,
  Moon,
  Pill,
  Scissors,
  AlertTriangle,
  Calendar,
  Link,
  CheckCircle,
  XCircle,
  Star,
  ArrowRight,
  Search,
  FileWarning,
  History,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stockStatusColors = {
  available: 'text-success',
  low: 'text-warning',
  out_of_stock: 'text-destructive',
};

const stockStatusLabels = {
  available: 'In Stock',
  low: 'Low Stock',
  out_of_stock: 'Out of Stock',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function MedicineSelector({ medicines, selectedMedicines, onSelect, onClose }) {
  const [search, setSearch] = useState('');
  const [showSubstitutes, setShowSubstitutes] = useState(null);
  
  const filtered = medicines.filter(m => 
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.generic_name.toLowerCase().includes(search.toLowerCase()) ||
    m.brand.toLowerCase().includes(search.toLowerCase())
  );

  const selectedIds = selectedMedicines.map(m => m.medicine_id);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search medicines..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 rounded-xl"
          data-testid="medicine-search-input"
        />
      </div>
      <ScrollArea className="h-[300px]">
        <div className="space-y-2">
          {filtered.map((medicine) => (
            <div key={medicine.id}>
              <div 
                className={`p-3 rounded-xl border cursor-pointer transition-colors ${
                  selectedIds.includes(medicine.id) 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:bg-muted/50'
                }`}
                onClick={() => {
                  if (!selectedIds.includes(medicine.id)) {
                    onSelect(medicine);
                  }
                }}
                data-testid={`select-medicine-${medicine.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {medicine.is_preferred && <Star className="w-4 h-4 text-warning fill-warning" />}
                      <span className="font-medium">{medicine.name}</span>
                      {selectedIds.includes(medicine.id) && (
                        <CheckCircle className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {medicine.generic_name} • {medicine.brand} • {medicine.strength}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${stockStatusColors[medicine.stock_status]}`}>
                      {stockStatusLabels[medicine.stock_status]}
                    </p>
                    <p className="text-sm text-muted-foreground">₹{medicine.price}</p>
                  </div>
                </div>
                {medicine.substitutes && medicine.substitutes.length > 0 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSubstitutes(showSubstitutes === medicine.id ? null : medicine.id);
                      }}
                      className="text-xs text-primary flex items-center gap-1"
                    >
                      <ArrowRight className="w-3 h-3" />
                      {medicine.substitutes.length} substitutes available
                    </button>
                    {showSubstitutes === medicine.id && (
                      <div className="mt-2 pl-4 border-l-2 border-primary/20 space-y-2">
                        {medicine.substitutes.map((sub) => (
                          <div 
                            key={sub.id}
                            className="p-2 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              const fullSub = medicines.find(m => m.id === sub.id);
                              if (fullSub && !selectedIds.includes(sub.id)) {
                                onSelect(fullSub);
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{sub.name}</span>
                              <span className={`text-xs ${stockStatusColors[sub.stock_status]}`}>
                                {stockStatusLabels[sub.stock_status]}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-muted-foreground py-8">No medicines found</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function MedicineItem({ item, medicines, onUpdate, onRemove }) {
  const medicine = medicines.find(m => m.id === item.medicine_id);
  
  return (
    <div className="p-4 rounded-xl border border-border bg-card" data-testid={`medicine-item-${item.medicine_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            {medicine?.is_preferred && <Star className="w-4 h-4 text-warning fill-warning" />}
            <span className="font-medium">{item.medicine_name}</span>
          </div>
          {medicine && (
            <p className="text-sm text-muted-foreground">
              {medicine.generic_name} • {medicine.brand} • {medicine.strength}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {medicine && (
            <Badge variant="outline" className={stockStatusColors[medicine.stock_status]}>
              {stockStatusLabels[medicine.stock_status]}
            </Badge>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid={`remove-medicine-${item.medicine_id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Dosage</Label>
          <Input
            value={item.dosage}
            onChange={(e) => onUpdate({ ...item, dosage: e.target.value })}
            placeholder="e.g., 1 tablet"
            className="rounded-lg text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Frequency</Label>
          <Input
            value={item.frequency}
            onChange={(e) => onUpdate({ ...item, frequency: e.target.value })}
            placeholder="e.g., twice daily"
            className="rounded-lg text-sm"
          />
        </div>
        <div>
          <Label className="text-xs">Duration</Label>
          <Input
            value={item.duration}
            onChange={(e) => onUpdate({ ...item, duration: e.target.value })}
            placeholder="e.g., 2 weeks"
            className="rounded-lg text-sm"
          />
        </div>
      </div>
      <div className="mt-2">
        <Label className="text-xs">Instructions</Label>
        <Input
          value={item.instructions}
          onChange={(e) => onUpdate({ ...item, instructions: e.target.value })}
          placeholder="e.g., Apply on affected area after washing"
          className="rounded-lg text-sm"
        />
      </div>
    </div>
  );
}

function PrescriptionSection({ title, icon: Icon, section, medicines, onUpdate, colorClass }) {
  const [isAddOpen, setIsAddOpen] = useState(false);

  const handleAddMedicine = (medicine) => {
    const newItem = {
      medicine_id: medicine.id,
      medicine_name: medicine.name,
      dosage: '',
      frequency: '',
      duration: '',
      instructions: ''
    };
    onUpdate({
      ...section,
      medicines: [...section.medicines, newItem]
    });
    setIsAddOpen(false);
  };

  const handleUpdateMedicine = (index, updatedItem) => {
    const newMedicines = [...section.medicines];
    newMedicines[index] = updatedItem;
    onUpdate({ ...section, medicines: newMedicines });
  };

  const handleRemoveMedicine = (index) => {
    const newMedicines = section.medicines.filter((_, i) => i !== index);
    onUpdate({ ...section, medicines: newMedicines });
  };

  return (
    <Card className="border-border shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className={`font-heading text-lg flex items-center gap-2 ${colorClass}`}>
            <Icon className="w-5 h-5" />
            {title}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAddOpen(true)}
            className="rounded-full"
            data-testid={`add-medicine-${title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Medicine
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {section.medicines.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">No medicines added yet</p>
        ) : (
          section.medicines.map((item, index) => (
            <MedicineItem
              key={`${item.medicine_id}-${index}`}
              item={item}
              medicines={medicines}
              onUpdate={(updated) => handleUpdateMedicine(index, updated)}
              onRemove={() => handleRemoveMedicine(index)}
            />
          ))
        )}
        <div className="pt-2">
          <Label className="text-xs">Section Notes</Label>
          <Textarea
            value={section.notes}
            onChange={(e) => onUpdate({ ...section, notes: e.target.value })}
            placeholder="Additional notes for this section..."
            className="rounded-xl text-sm min-h-[60px]"
          />
        </div>
      </CardContent>

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Medicine to {title}</DialogTitle>
            <DialogDescription>Select a medicine from inventory</DialogDescription>
          </DialogHeader>
          <MedicineSelector
            medicines={medicines}
            selectedMedicines={section.medicines}
            onSelect={handleAddMedicine}
            onClose={() => setIsAddOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function PrescriptionEditorPage() {
  const { prescriptionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [prescription, setPrescription] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [morningRoutine, setMorningRoutine] = useState({ medicines: [], notes: '' });
  const [eveningRoutine, setEveningRoutine] = useState({ medicines: [], notes: '' });
  const [oralMedicines, setOralMedicines] = useState({ medicines: [], notes: '' });
  const [procedures, setProcedures] = useState('');
  const [precautions, setPrecautions] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [mediaLinks, setMediaLinks] = useState([]);
  const [newMediaLink, setNewMediaLink] = useState('');

  // Modification reason
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [modificationReason, setModificationReason] = useState('');
  const [modificationReasonText, setModificationReasonText] = useState('');
  const [isFirstSave, setIsFirstSave] = useState(true);

  // Suggested media
  const [suggestedMedia, setSuggestedMedia] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const MODIFICATION_REASONS = [
    { value: 'no_response', label: 'No Response' },
    { value: 'side_effects', label: 'Side Effects' },
    { value: 'stock_unavailable', label: 'Stock Unavailable' },
    { value: 'budget_issue', label: 'Budget Issue' },
    { value: 'diagnosis_revised', label: 'Diagnosis Revised' },
    { value: 'maintenance_phase', label: 'Maintenance Phase' },
  ];

  const fetchPrescription = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/prescriptions/${prescriptionId}`, { withCredentials: true });
      setPrescription(data);
      setMorningRoutine(data.morning_routine || { medicines: [], notes: '' });
      setEveningRoutine(data.evening_routine || { medicines: [], notes: '' });
      setOralMedicines(data.oral_medicines || { medicines: [], notes: '' });
      setProcedures(data.procedures || '');
      setPrecautions(data.precautions || '');
      setFollowUpDate(data.follow_up_date || '');
      setMediaLinks(data.media_links || []);
      // If prescription already has medicines, it's not the first save
      const totalMeds = (data.morning_routine?.medicines?.length || 0) + (data.evening_routine?.medicines?.length || 0) + (data.oral_medicines?.medicines?.length || 0);
      setIsFirstSave(totalMeds === 0 && !data.procedures && !data.precautions);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch prescription');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [prescriptionId, navigate]);

  const fetchMedicines = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/medicines?is_active=true`, { withCredentials: true });
      // Fetch substitutes for each medicine
      const medicinesWithSubs = await Promise.all(data.map(async (med) => {
        try {
          const { data: detail } = await axios.get(`${API_URL}/api/medicines/${med.id}`, { withCredentials: true });
          return { ...med, substitutes: detail.substitutes || [] };
        } catch {
          return med;
        }
      }));
      setMedicines(medicinesWithSubs);
    } catch (error) {
      console.error('Failed to fetch medicines', error);
    }
  }, []);

  useEffect(() => {
    fetchPrescription();
    fetchMedicines();
  }, [fetchPrescription, fetchMedicines]);

  // Fetch suggested media based on patient's concern tags
  useEffect(() => {
    const fetchSuggestedMedia = async () => {
      if (!prescription?.patient_id) return;
      setLoadingSuggestions(true);
      try {
        // First get patient to get concern tags
        const { data: patient } = await axios.get(`${API_URL}/api/patients/${prescription.patient_id}`, { withCredentials: true });
        const tags = patient.concern_tags || [];
        if (tags.length === 0) {
          setSuggestedMedia([]);
          setLoadingSuggestions(false);
          return;
        }
        const { data: suggestions } = await axios.get(`${API_URL}/api/media-library/suggestions`, {
          params: { concern_tags: tags.join(',') },
          withCredentials: true
        });
        // Filter out media already in the prescription's media_links
        const filtered = suggestions.filter(s => !mediaLinks.includes(s.url));
        setSuggestedMedia(filtered);
      } catch (error) {
        console.error('Failed to fetch media suggestions', error);
      } finally {
        setLoadingSuggestions(false);
      }
    };
    fetchSuggestedMedia();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prescription?.patient_id]);

  const handleSave = async () => {
    // If editing an existing prescription, show reason modal first
    if (!isFirstSave && !showReasonModal) {
      setShowReasonModal(true);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        morning_routine: morningRoutine,
        evening_routine: eveningRoutine,
        oral_medicines: oralMedicines,
        procedures,
        precautions,
        follow_up_date: followUpDate || null,
        media_links: mediaLinks
      };
      // Add modification reason if this is an edit
      if (!isFirstSave && modificationReason) {
        payload.modification_reason = modificationReason;
        payload.modification_reason_text = modificationReasonText;
      }
      await axios.put(`${API_URL}/api/prescriptions/${prescriptionId}`, payload, { withCredentials: true });
      toast.success('Prescription saved');
      setShowReasonModal(false);
      setModificationReason('');
      setModificationReasonText('');
      setIsFirstSave(false);
      fetchPrescription();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save prescription');
    } finally {
      setSaving(false);
    }
  };

  const addMediaLink = () => {
    if (newMediaLink.trim()) {
      setMediaLinks([...mediaLinks, newMediaLink.trim()]);
      setNewMediaLink('');
    }
  };

  const removeMediaLink = (index) => {
    setMediaLinks(mediaLinks.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="prescription-editor-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!prescription) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Prescription not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="prescription-editor-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
            Prescription Editor
          </h1>
          <p className="text-muted-foreground mt-1">
            {prescription.patient_name} • Created by {prescription.doctor_name}
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-primary hover:bg-primary/90 rounded-full"
          data-testid="save-prescription-button"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Prescription
        </Button>
      </div>

      {/* Patient allergies warning */}
      {prescription.patient?.allergies?.length > 0 && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Patient Allergies</p>
              <p className="text-sm">{prescription.patient.allergies.join(', ')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Morning Routine */}
        <PrescriptionSection
          title="Morning Routine"
          icon={Sun}
          section={morningRoutine}
          medicines={medicines}
          onUpdate={setMorningRoutine}
          colorClass="text-warning"
        />

        {/* Evening Routine */}
        <PrescriptionSection
          title="Evening Routine"
          icon={Moon}
          section={eveningRoutine}
          medicines={medicines}
          onUpdate={setEveningRoutine}
          colorClass="text-info"
        />

        {/* Oral Medicines */}
        <PrescriptionSection
          title="Oral Medicines"
          icon={Pill}
          section={oralMedicines}
          medicines={medicines}
          onUpdate={setOralMedicines}
          colorClass="text-primary"
        />

        {/* Procedures */}
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2 text-secondary">
              <Scissors className="w-5 h-5" />
              Procedures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={procedures}
              onChange={(e) => setProcedures(e.target.value)}
              placeholder="List any procedures recommended..."
              className="rounded-xl min-h-[120px]"
              data-testid="procedures-input"
            />
          </CardContent>
        </Card>
      </div>

      {/* Precautions & Follow-up */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Precautions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={precautions}
              onChange={(e) => setPrecautions(e.target.value)}
              placeholder="Important precautions for the patient..."
              className="rounded-xl min-h-[120px]"
              data-testid="precautions-input"
            />
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2 text-success">
              <Calendar className="w-5 h-5" />
              Follow-up Date
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              className="rounded-xl"
              data-testid="follow-up-date-input"
            />
          </CardContent>
        </Card>
      </div>

      {/* Media Links */}
      <Card className="border-border shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Link className="w-5 h-5" />
            Media Links
          </CardTitle>
          <CardDescription>Add reference links, videos, or educational resources</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={newMediaLink}
              onChange={(e) => setNewMediaLink(e.target.value)}
              placeholder="Paste URL here..."
              className="rounded-xl"
              data-testid="media-link-input"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMediaLink())}
            />
            <Button 
              onClick={addMediaLink} 
              variant="outline" 
              className="rounded-full"
              data-testid="add-media-link-button"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {mediaLinks.length > 0 && (
            <div className="space-y-2">
              {mediaLinks.map((link, index) => (
                <div key={index} className="flex items-center gap-2 p-3 rounded-xl bg-muted/50">
                  <Link className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <a 
                    href={link} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm text-primary hover:underline truncate flex-1"
                  >
                    {link}
                  </a>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeMediaLink(index)}
                    className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                    data-testid={`remove-media-link-${index}`}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Suggested Media Section */}
          {loadingSuggestions && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading suggestions...
            </div>
          )}
          {!loadingSuggestions && suggestedMedia.length > 0 && (
            <div className="pt-3 border-t border-border" data-testid="suggested-media-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-violet-500" />
                <p className="text-xs font-bold text-violet-600 uppercase tracking-wider">Suggested for this patient</p>
              </div>
              <div className="space-y-2">
                {suggestedMedia.map((media) => (
                  <div key={media.id} className="flex items-center gap-3 p-3 rounded-xl bg-violet-50/50 border border-violet-100 hover:bg-violet-50 transition-colors" data-testid={`suggested-media-${media.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{media.patient_facing_title || media.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{media.media_type}</Badge>
                        {media.concern_tags?.map(tag => (
                          <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 bg-violet-100 text-violet-700">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setMediaLinks(prev => [...prev, media.url]);
                        setSuggestedMedia(prev => prev.filter(s => s.id !== media.id));
                        toast.success(`Added "${media.title}"`);
                      }}
                      className="rounded-full text-violet-600 hover:text-violet-700 hover:bg-violet-100 flex-shrink-0"
                      data-testid={`add-suggested-media-${media.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modification Log */}
      {prescription.modification_log?.length > 0 && (
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <History className="w-5 h-5" />
              Modification History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {prescription.modification_log.map((entry, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 text-sm" data-testid={`modification-log-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium capitalize">{entry.reason.replace(/_/g, ' ')}</p>
                    {entry.reason_text && <p className="text-muted-foreground text-xs mt-0.5">{entry.reason_text}</p>}
                    <p className="text-xs text-muted-foreground mt-1">By {entry.modified_by_name} · {new Date(entry.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Draft Comparison */}
      {prescription.ai_draft && (
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              AI Draft vs Final
              {prescription.ai_draft_diff?.has_changes && (
                <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-200">
                  {prescription.ai_draft_diff.total_changes} changes
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {prescription.ai_override_log && (
              <div className="p-3 rounded-xl bg-violet-50 border border-violet-100 mb-4">
                <p className="text-xs font-bold uppercase tracking-wider text-violet-500 mb-1">Override Reason</p>
                <p className="text-sm font-medium text-violet-800 capitalize">{prescription.ai_override_log.reason?.replace(/_/g, ' ') || 'Not specified'}</p>
                {prescription.ai_override_log.text && <p className="text-xs text-violet-600 mt-1">{prescription.ai_override_log.text}</p>}
              </div>
            )}
            {prescription.ai_draft_diff && (
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="p-3 rounded-xl bg-emerald-50">
                  <p className="text-xl font-heading font-semibold text-emerald-700">{prescription.ai_draft_diff.medicines_added?.length || 0}</p>
                  <p className="text-xs text-emerald-600">Added</p>
                </div>
                <div className="p-3 rounded-xl bg-rose-50">
                  <p className="text-xl font-heading font-semibold text-rose-700">{prescription.ai_draft_diff.medicines_removed?.length || 0}</p>
                  <p className="text-xs text-rose-600">Removed</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <p className="text-xl font-heading font-semibold text-amber-700">{prescription.ai_draft_diff.medicines_changed?.length || 0}</p>
                  <p className="text-xs text-amber-600">Changed</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modification Reason Modal */}
      <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
        <DialogContent className="sm:max-w-md" data-testid="modification-reason-modal">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-warning" />
              Modification Reason
            </DialogTitle>
            <DialogDescription>Why is this prescription being modified?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {MODIFICATION_REASONS.map((r) => (
                <Button
                  key={r.value}
                  variant={modificationReason === r.value ? 'default' : 'outline'}
                  className={`rounded-xl text-xs h-auto py-2 ${modificationReason === r.value ? 'bg-primary' : ''}`}
                  onClick={() => setModificationReason(r.value)}
                  data-testid={`reason-${r.value}`}
                >
                  {r.label}
                </Button>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Additional details (optional)</Label>
              <Textarea
                value={modificationReasonText}
                onChange={(e) => setModificationReasonText(e.target.value)}
                placeholder="Free text reason or additional details..."
                className="rounded-xl min-h-[60px] text-sm"
                data-testid="modification-reason-text"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReasonModal(false); setModificationReason(''); setModificationReasonText(''); }} className="rounded-full">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={!modificationReason && !modificationReasonText}
              className="bg-primary hover:bg-primary/90 rounded-full"
              data-testid="confirm-save-with-reason"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save with Reason
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
